import React, { useContext } from "react";
import { useSelector } from "react-redux";

import { useMedplum } from "@medplum/react";

import {
  Accordion,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { IconEdit, IconPlus, IconReportMedical, IconTrash } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { useAppDispatch } from "@/store";
import {
  deleteProcedure,
  selectAllActiveProcedures,
  selectTargetProcedure,
  setTargetProcedure,
  updateProcedure,
} from "@/store/slices/procedures";
import { QuestionnaireResponseStatus } from "@/utils/enums";
import { ProceduresFormType, ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

import { softDeleteProcedureQuestionnaireResponse } from "../services/questionnaire-response";

type EncounterProceduresAccordionProps<T> = {
  form: UseFormReturnType<ProceduresFormType>;
  open: (id: T) => void;
  drawerName: T;
  closeAll: () => void;
  isEncounterActive: boolean;
  isAmendment: boolean;
  markProceduresListSaved?: () => void;
  markDrawerFormSaved?: () => void;
};

const EncounterProceduresAccordion = <T,>({
  form,
  open,
  drawerName,
  closeAll,
  isEncounterActive,
  isAmendment,
  markProceduresListSaved,
  markDrawerFormSaved,
}: EncounterProceduresAccordionProps<T>) => {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const [opened, handlers] = useDisclosure(false);
  const { isPatient } = useContext(ProfileContext);
  const procedures = useSelector(selectAllActiveProcedures);
  const { hasProviderOrScribeRole } = usePractitionerPermissions();
  const targetProcedure = useSelector(selectTargetProcedure);

  const onEdit = (
    event: React.MouseEvent<HTMLButtonElement>,
    cptCode: ProceduresFormTypeWithId,
  ) => {
    event.stopPropagation();
    dispatch(setTargetProcedure(cptCode as ProceduresFormTypeWithId));
    open(drawerName);
  };

  const onDelete = async () => {
    if (targetProcedure && targetProcedure.exists) {
      await softDeleteProcedureQuestionnaireResponse(medplum, targetProcedure.id);
      dispatch(
        updateProcedure({
          id: targetProcedure.id,
          changes: { status: QuestionnaireResponseStatus.Stopped },
        }),
      );
    } else {
      dispatch(deleteProcedure(targetProcedure?.id ?? ""));
    }
    dispatch(setTargetProcedure(undefined));
    handlers.close();
    form.reset();
    markProceduresListSaved?.();
    markDrawerFormSaved?.();
  };

  const onAddMore = () => {
    dispatch(setTargetProcedure(undefined));
    open(drawerName);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <TextWrapper c="dark.9" variant="bold">
          Procedures
        </TextWrapper>
        {(isEncounterActive || isAmendment) && hasProviderOrScribeRole && !isPatient && (
          <Button
            variant="outline"
            color="burgundy.9"
            onClick={onAddMore}
            leftSection={<IconPlus size={16} />}
            data-testid="encounter-add-procedure-button"
          >
            Add More
          </Button>
        )}
      </Group>
      <Paper p="md" bg="white" bd="1px solid var(--mantine-color-cream-4)">
        <Accordion w="100%" multiple variant="separated">
          {procedures?.map((procedure) => (
            <React.Fragment key={procedure.id ?? procedure.cptCode?.code}>
              <Modal
                centered
                opened={opened}
                withCloseButton={true}
                onClose={handlers.close}
                styles={{ close: { top: -12 } }}
                title={
                  <TextWrapper size="xl" c="burgundy.9" variant="bold">
                    Are you sure you want to remove this code?
                  </TextWrapper>
                }
              >
                <Stack>
                  <TextWrapper>This code will be removed from the encounter.</TextWrapper>
                  <Group pt="sm" justify="flex-end" mx={{ base: "xs", xs: 0 }}>
                    <Button fw="bold" variant="outline" onClick={handlers.close}>
                      Cancel
                    </Button>
                    <Button fw="bold" bg="red.6" color="white" variant="outline" onClick={onDelete}>
                      Yes, Delete Code
                    </Button>
                  </Group>
                </Stack>
              </Modal>

              <Accordion.Item
                bd="1px solid var(--mantine-color-cream-4)"
                key={procedure.id ?? procedure.cptCode?.code}
                value={procedure.id ?? procedure.cptCode?.code}
                data-testid={`encounter-procedure-item-${procedure.cptCode?.code ?? procedure.id ?? "unknown"}`}
              >
                <Accordion.Control
                  bg="white"
                  style={{ borderRadius: 8 }}
                  data-testid={`encounter-procedure-toggle-${procedure.cptCode?.code ?? procedure.id ?? "unknown"}`}
                >
                  <Group w="100%" wrap="nowrap" justify="space-between">
                    <Group w="70%" gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                      <IconReportMedical
                        size={20}
                        style={{ flexShrink: 0 }}
                        color="var(--mantine-color-gray-6)"
                      />
                      <Tooltip label={procedure.cptCode?.display}>
                        <TextWrapper
                          size="md"
                          fw="bold"
                          c="gray.8"
                          style={{ wordBreak: "break-word" }}
                        >
                          {procedure.cptCode?.code} - {procedure.cptCode?.display}
                        </TextWrapper>
                      </Tooltip>
                    </Group>
                    {/* A client sees their own recorded procedures but never edits or removes
                    them, regardless of the encounter's state. */}
                    {(isEncounterActive || isAmendment) && !isPatient && (
                      <Group mr="xs" gap="md" wrap="nowrap">
                        <PermissionGate requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
                          <UnstyledButton
                            display="flex"
                            onClick={(event) => onEdit(event, procedure)}
                            data-testid={`encounter-procedure-edit-${procedure.cptCode?.code ?? procedure.id ?? "unknown"}`}
                          >
                            <IconEdit size={20} />
                            <TextWrapper ml="xs" fw={600}>
                              Edit
                            </TextWrapper>
                          </UnstyledButton>
                        </PermissionGate>
                        <PermissionGate requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
                          <UnstyledButton
                            color="red.8"
                            display="flex"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlers.open();
                              dispatch(setTargetProcedure(procedure));
                            }}
                          >
                            <IconTrash size={20} color="var(--mantine-color-red-8)" />
                            <TextWrapper ml="xs" fw={600} c="red.8">
                              Delete
                            </TextWrapper>
                          </UnstyledButton>
                        </PermissionGate>
                      </Group>
                    )}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack pt="sm">
                    {Object.values(procedure?.cptDiagnosesFormset ?? {}).map((diagnosis) => (
                      <AccordionItem
                        label="ICD-10"
                        key={diagnosis?.cptDiagnosesFormsetDiagnosis?.code}
                        value={`${diagnosis?.cptDiagnosesFormsetDiagnosis?.code} - ${diagnosis?.cptDiagnosesFormsetDiagnosis?.display}`}
                      />
                    ))}

                    {procedure?.cptCodeNotes && (
                      <AccordionItem label="Note" value={procedure?.cptCodeNotes} />
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </React.Fragment>
          ))}
        </Accordion>
      </Paper>
    </Stack>
  );
};

const AccordionItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <Group py="xs" px="lg" bg="gray.0" justify="space-between">
      <TextWrapper fw="bold" c="dark.9">
        {label}
      </TextWrapper>
      <TextWrapper c="dark.9">{value}</TextWrapper>
    </Group>
  );
};

export default EncounterProceduresAccordion;
